import { PriceTier, serializePriceTier } from '../../../models/PriceTier';
import { BulkAddProductsToShoppingCartResponse, ProductFilter } from '../../store/api';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { useState } from 'react';
import { SubscriptionPeriodType } from '../../../models/Article';
import { addProductsFromBundleByFilter } from './addProductsFromBundleByFilter';
import { SOURCE_BUNDLE } from '../../store/constants';
import { bulkUpdateToCart } from './bulkUpdateToCart';
import {
    isExcludeProductSelection,
    isIncludeProductSelection,
    ProductSelection
} from '../../shared/product-selection/ProductSelectionTypes';
import { addProductsFromBundleByIds } from './addProductsFromBundleByIds';
import { useNotification } from '../../app-store/notification/useNotification';

type GetRequestParams = {
    bundleId: string;
    priceTier: PriceTier;
    subscriptionPeriodType: SubscriptionPeriodType;
    performChecks: boolean;
    filterRequest: ProductFilter;
    productSelection: ProductSelection;
};

const buildAddProductsCallback = (
    { bundleId, priceTier, subscriptionPeriodType, performChecks, filterRequest, productSelection }: GetRequestParams,
    t: TFunction
): (() => Promise<BulkAddProductsToShoppingCartResponse>) => {
    return () => {
        if (isIncludeProductSelection(productSelection)) {
            return addProductsFromBundleByIds({
                body: {
                    productIds: productSelection.included.map(value => value.productId),
                    targetPrice: serializePriceTier(priceTier)!,
                    subscriptionPeriodType,
                    checkEpress: false
                },
                params: {
                    performChecks,
                    bundleId
                }
            });
        } else if (isExcludeProductSelection(productSelection)) {
            const body = {
                filterRequest,
                excludedProductIds: productSelection.excluded.map(({ productId }) => productId),
                targetPrice: serializePriceTier(priceTier)!,
                subscriptionPeriodType
            };

            return addProductsFromBundleByFilter({
                bundleId,
                body,
                params: {
                    performChecks
                }
            });
        } else {
            throw new Error(t('notifications.buildAddProductsCallback.error'));
        }
    };
};

export const useAddFromBundleToCart = (productFilter: ProductFilter, onSuccess: () => void) => {
    const { t } = useTranslation();
    const [isLoading, setLoading] = useState(false);
    const { showNotificationWithInfo } = useNotification();

    return {
        isLoading,
        addFromBundleToCartBulk: (
            bundleId: string,
            priceTier: PriceTier,
            subscriptionPeriodType: SubscriptionPeriodType,
            performChecks: boolean,
            productSelection?: ProductSelection
        ) => {
            const apiCallback = buildAddProductsCallback(
                {
                    bundleId,
                    priceTier,
                    subscriptionPeriodType,
                    performChecks,
                    filterRequest: productFilter,
                    productSelection
                },
                t
            );

            return bulkUpdateToCart({
                setLoading,
                performChecks,
                source: SOURCE_BUNDLE,
                apiCallback,
                t,
                onSuccess,
                productSelection,
                showNotificationWithInfo
            });
        }
    };
};
